import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Chip,
    List,
    ListItem,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    Paper,
    IconButton,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { fetchPlansForCustomer, fetchSubscription } from "../utils/api"
import { Alert } from "react-bootstrap";
import { formatUtcToLocal } from "../utils/PageUtils";

const STATUS_STYLES = {
    Active: { bg: "#e7f6ec", color: "#0a8a3a", dot: "#0a8a3a" },
    "Awaiting Renewal": { bg: "#fff4e5", color: "#b5650a", dot: "#b5650a" },
    Suspended: { bg: "#fdecea", color: "#c0392b", dot: "#c0392b" },
    Draft: { bg: "#eef0f2", color: "#5f6368", dot: "#5f6368" },
};

const getStatusStyle = (status) => STATUS_STYLES[status] || STATUS_STYLES.Draft;

export default function Subscription({ userRole, bearerToken }) {
    const [subscription, setSubscription] = useState({});
    const [allPlans, setAllPlans] = useState([]);
    const [plansLoaded, setPlansLoaded] = useState(false);
    const [plansLoading, setPlansLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchSubscriptionDetails = async () => {
            const subscriptionResponse = await fetchSubscription(bearerToken)
            if (subscriptionResponse.success) {
                setSubscription(subscriptionResponse.data.records[0]);
            } else {
                setError(subscriptionResponse.message || "Failed to fetch subscription details");
            }
        };
        fetchSubscriptionDetails();
    }, []);

    const handleExplorePlans = async () => {
        setOpenModal(true);
        if (plansLoaded) return; // already have plans, don't refetch
        setPlansLoading(true);
        try {
            const plansResponse = await fetchPlansForCustomer(bearerToken);
            if (plansResponse.success) {
                setAllPlans(plansResponse.data.records);
                setPlansLoaded(true);
            } else {
                setError(plansResponse.message || "Failed to fetch subscription plans");
            }
        } finally {
            setPlansLoading(false);
        }
    };

    const statusStyle = getStatusStyle(subscription?.status);

    if (userRole !== "customer") {
        return (
            <div className="user-error">
                <h1>404 - Page Not Found</h1>
                <p>The page you're looking for doesn't exist.</p>
            </div>
        );
    }

    return (
        <>
            {error && (
                <Alert variant="danger" style={{
                    width: "90%",
                    margin: "30px auto",
                }}>
                    {error}
                </Alert>
            )}
            {
                !subscription && (
                    <Alert variant="danger" style={{
                        width: "90%",
                        margin: "30px auto",
                    }}>
                        You do not have a subscription. Please contact support to set one up before using the application.
                    </Alert>
                )
            }
            {
                !error && subscription && (
                    <Box sx={{ maxWidth: 680, mx: "auto", my: 4, fontFamily: "Inter, sans-serif", display: "flex", flexDirection: "column", gap: 2, px: 2 }}>

                        {/* Subscription Card */}
                        <Box
                            sx={{
                                borderRadius: 3,
                                border: "1px solid #cfe3ff",
                                background: "#eef6ff",
                                p: { xs: 3, md: 4 },
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                textAlign: "left",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    flexWrap: "wrap",
                                    gap: 1,
                                }}
                            >
                                <Box>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "#6b7280",
                                            fontWeight: 500,
                                            letterSpacing: 0.6,
                                            display: "block",
                                            mb: 0.5,
                                        }}
                                    >
                                        YOUR CURRENT PLAN
                                    </Typography>

                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontSize: { xs: 22, md: 26 },
                                            fontWeight: 700,
                                        }}
                                    >
                                        {subscription?.plan_name}
                                    </Typography>
                                </Box>

                                <Chip
                                    label={subscription?.status}
                                    sx={{
                                        backgroundColor: statusStyle.bg,
                                        color: statusStyle.color,
                                        fontWeight: 600,
                                        fontSize: 13,
                                        "& .MuiChip-label": { display: "flex", alignItems: "center", gap: "6px" },
                                    }}
                                    icon={
                                        <span
                                            style={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: "50%",
                                                background: statusStyle.dot,
                                                marginLeft: 8,
                                            }}
                                        />
                                    }
                                />
                            </Box>
                            {subscription?.status === "Draft" ? (
                                <Box sx={{ background: "#ffffff", borderRadius: 2, p: 2, mt: 1, textAlign: "center" }}>
                                    <Typography sx={{ fontSize: 13.5, color: "#6b7280" }}>
                                        This subscription hasn't been activated yet.
                                    </Typography>
                                </Box>
                            ) :
                                (
                                    < Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mt: 1 }}>
                                        <Box sx={{ background: "#ffffff", borderRadius: 2, p: 1.5, flex: 1 }}>
                                            <Typography sx={{ fontSize: 12, color: "#4b5563", mb: 0.5 }}>Billed</Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{subscription?.billing_cycle}</Typography>
                                        </Box>
                                        <Box sx={{ background: "#ffffff", borderRadius: 2, p: 1.5, flex: 1 }}>
                                            <Typography sx={{ fontSize: 12, color: "#4b5563", mb: 0.5 }}>Subscription start</Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatUtcToLocal(subscription?.plan_start_date)}</Typography>
                                        </Box>
                                        <Box sx={{ background: "#ffffff", borderRadius: 2, p: 1.5, flex: 1 }}>
                                            <Typography sx={{ fontSize: 12, color: "#4b5563", mb: 0.5 }}>Renews on</Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatUtcToLocal(subscription?.plan_end_date)}</Typography>
                                        </Box>
                                    </Box>
                                )}
                        </Box>

                        {/* Upgrade Section */}
                        <Box
                            sx={{
                                p: { xs: 3, md: 3.5 },
                                borderRadius: 3,
                                background: "#f8f9fa",
                                border: "1px solid #e5e7eb",
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                            }}
                        >
                            <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                                <Typography sx={{ fontWeight: 600, fontSize: { xs: 15, md: 16 } }}>
                                    Need a change?
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: "#6b7280", mt: 0.25 }}>
                                    We're here to help.
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    flexDirection: { xs: "column", sm: "row" },
                                    width: { xs: "100%", sm: "auto" },
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    sx={{
                                        minWidth: 160,
                                        py: 1.2,
                                        fontSize: 14,
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        borderColor: "#2d57b2",
                                        color: "#374151",
                                        textTransform: "none",
                                    }}
                                    onClick={handleExplorePlans}
                                >
                                    Explore plans
                                </Button>
                                <Button
                                    variant="contained"
                                    disableElevation
                                    sx={{
                                        minWidth: 160,
                                        py: 1.2,
                                        fontSize: 14,
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        background: "#2d57b2",
                                        textTransform: "none",
                                    }}
                                    onClick={() => window.open(process.env.REACT_APP_SUPPORT_URL, "_blank")}
                                >
                                    Chat with us
                                </Button>
                            </Box>
                        </Box>

                        {/* Plans Modal with Close Icon and Discount Message */}
                        <Dialog
                            open={openModal}
                            onClose={() => setOpenModal(false)}
                            maxWidth="xl"
                            fullWidth
                            PaperProps={{
                                sx: {
                                    width: '95vw',
                                    maxWidth: 1500,
                                    borderRadius: 3,
                                }
                            }}
                        >
                            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3 }}>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>All plans</Typography>
                                <IconButton onClick={() => setOpenModal(false)}><CloseIcon /></IconButton>
                            </DialogTitle>
                            <DialogContent sx={{ mt: 1, px: { xs: 2, md: 3 } }}>
                                {plansLoading ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <>
                                        {/* Discount badge */}
                                        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                                            <Typography sx={{
                                                background: "#eef6ff",
                                                color: "#0047b3",
                                                fontWeight: 600,
                                                px: 2,
                                                py: 0.75,
                                                borderRadius: 2,
                                                fontSize: { xs: 13, md: 14 },
                                                border: "1px solid #cfe3ff",
                                            }}>
                                                Get 2 months free with annual billing
                                            </Typography>
                                        </Box>

                                        {/* Plans Grid */}
                                        <Grid container spacing={3} justifyContent="center" sx={{ pt: 1.5 }}>
                                            {[...allPlans]
                                                .sort((a, b) => Number(a.monthly_price) - Number(b.monthly_price))
                                                .map((plan) => {
                                                    const isCurrentPlan = plan.plan_id === subscription?.subscription_plan_id;

                                                    return (
                                                        <Grid item xs={12} sm={6} md={4} lg={3} key={plan.name || plan.plan_id}>
                                                            <Paper
                                                                elevation={0}
                                                                sx={{
                                                                    borderRadius: 3,
                                                                    border: isCurrentPlan ? "2px solid #2d57b2" : "1px solid #e5e7eb",
                                                                    position: "relative",
                                                                    height: "100%",
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    minHeight: 380,
                                                                }}
                                                            >
                                                                {isCurrentPlan && (
                                                                    <Chip
                                                                        label="CURRENT PLAN"
                                                                        size="small"
                                                                        sx={{
                                                                            position: "absolute",
                                                                            top: 0,
                                                                            left: "50%",
                                                                            transform: "translate(-50%, -50%)",
                                                                            backgroundColor: "#2d57b2",
                                                                            color: "white",
                                                                            fontWeight: 600,
                                                                            fontSize: 11,
                                                                            height: 22,
                                                                            p: 1.7,
                                                                            zIndex: 1,
                                                                            pointerEvents: "none",
                                                                        }}
                                                                    />
                                                                )}

                                                                <Box
                                                                    sx={{
                                                                        textAlign: "center",
                                                                        p: 2.5,
                                                                        pt: 3.5,
                                                                        background: "#f0f4f9",
                                                                        borderBottom: "1px solid #e5e7eb",
                                                                        borderTopLeftRadius: 10,
                                                                        borderTopRightRadius: 10,
                                                                    }}
                                                                >
                                                                    <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 2 }}>
                                                                        {plan.name.toUpperCase()}
                                                                    </Typography>

                                                                    <Box>
                                                                        <Typography sx={{ fontSize: 30, fontWeight: 700, color: "#111111" }}>
                                                                            <span style={{ fontSize: 14 }}>$</span>
                                                                            {plan.monthly_price}
                                                                            <span style={{ fontSize: 15, color: "#6b7280", fontWeight: 400 }}>
                                                                                /mo
                                                                            </span>
                                                                        </Typography>

                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: 14,
                                                                                color: "#6b7280",
                                                                                fontWeight: 500,
                                                                                mt: 1
                                                                            }}
                                                                        >
                                                                            Billed annually:
                                                                            <strong style={{ marginLeft: 4, color: "#111111" }}>
                                                                                ${plan.annual_price}
                                                                            </strong>
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>

                                                                <Box sx={{ p: 2.5, background: "#ffffff", flex: 1, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }}>
                                                                    <List dense sx={{ p: 0, textAlign: "left" }}>
                                                                        <ListItem sx={{ py: 0.5 }}>
                                                                            <CheckCircleIcon sx={{ color: "#0a8a3a", mr: 1, fontSize: 20 }} />
                                                                            <ListItemText
                                                                                primary={`Monthly credits: ${plan.monthly_credits || 0}`}
                                                                            />
                                                                        </ListItem>

                                                                        {plan.credit_costs && Object.keys(plan.credit_costs).length > 0 && (
                                                                            <>
                                                                                <ListItem sx={{ py: 0.5 }}>
                                                                                    <CheckCircleIcon sx={{ color: "#0a8a3a", mr: 1, fontSize: 20 }} />
                                                                                    <ListItemText
                                                                                        primary="Credit costs"
                                                                                    />
                                                                                </ListItem>

                                                                                <List dense disablePadding sx={{ pl: 4.5 }}>
                                                                                    {Object.entries(plan.credit_costs)
                                                                                        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                                                                                        .map(([key, value]) => (
                                                                                            <ListItem key={key} sx={{ py: 0.25 }}>
                                                                                                <ListItemText
                                                                                                    primary={`${key}: ${value} credit${value > 1 ? "s" : ""}`}
                                                                                                />
                                                                                            </ListItem>
                                                                                        ))}
                                                                                </List>
                                                                            </>
                                                                        )}

                                                                        <ListItem sx={{ py: 0.5 }}>
                                                                            <CheckCircleIcon sx={{ color: "#0a8a3a", mr: 1, fontSize: 20 }} />
                                                                            <ListItemText
                                                                                primary={`Extra credits: $${plan?.credit_overage_price || plan?.overage_price || 0} per credit`}
                                                                            />
                                                                        </ListItem>

                                                                    </List>
                                                                </Box>
                                                            </Paper>
                                                        </Grid>
                                                    )
                                                })}
                                        </Grid>
                                    </>
                                )}
                            </DialogContent>
                        </Dialog>

                    </Box >
                )}
        </>
    );
}